import { Injectable, inject } from '@angular/core';
import { MatSnackBar, MatSnackBarRef, TextOnlySnackBar } from '@angular/material/snack-bar';

import { userFacingRejection } from '@core/http-interceptors/user-facing-rejection';

import {
  AbookSnackbarComponent,
  AbookSnackbarData,
} from './abook-snackbar-component/abook-snackbar.component';

export interface SnackbarConfig {
  duration?: number;
  horizontalPosition?: 'start' | 'center' | 'end' | 'left' | 'right';
  verticalPosition?: 'top' | 'bottom';
}

export interface ConfirmationConfig extends SnackbarConfig {
  actionText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class AbookSnackbarService {
  private readonly snackBar = inject(MatSnackBar);

  private readonly defaultConfig: SnackbarConfig = {
    duration: 3000,
    horizontalPosition: 'center',
    verticalPosition: 'bottom',
  };

  /**
   * Show a simple success message
   */
  showSuccess(message: string, config?: SnackbarConfig): MatSnackBarRef<TextOnlySnackBar> {
    const finalConfig = { ...this.defaultConfig, ...config };

    return this.snackBar.open(message, 'Luk', {
      duration: finalConfig.duration,
      horizontalPosition: finalConfig.horizontalPosition,
      verticalPosition: finalConfig.verticalPosition,
      panelClass: ['success-snackbar'],
    });
  }

  /**
   * Show an error message with title and detailed message
   */
  showError(title: string, message: string, config?: SnackbarConfig): MatSnackBarRef<AbookSnackbarComponent> {
    const finalConfig = { ...this.defaultConfig, duration: 5000, ...config };

    return this.snackBar.openFromComponent(AbookSnackbarComponent, {
      data: {
        title,
        message,
        actionDismissText: 'OK',
      },
      duration: finalConfig.duration,
      horizontalPosition: finalConfig.horizontalPosition,
      verticalPosition: finalConfig.verticalPosition,
      panelClass: ['error-snackbar'],
    });
  }

  /**
   * Report a failed call as "Fejl" with `message` — unless the API rejected it with a user-facing
   * reason, which `AuthInterceptor` has already shown; a generic line on top of it is only noise.
   */
  showFailure(error: unknown, message: string): void {
    if (!userFacingRejection(error)) {
      this.showError('Fejl', message);
    }
  }

  /**
   * Show a warning message with title and detailed message
   */
  showWarning(title: string, message: string, config?: SnackbarConfig): MatSnackBarRef<AbookSnackbarComponent> {
    const finalConfig = { ...this.defaultConfig, duration: 8000, ...config };

    return this.snackBar.openFromComponent(AbookSnackbarComponent, {
      data: {
        title,
        message,
        actionDismissText: 'Luk',
      },
      duration: finalConfig.duration,
      horizontalPosition: finalConfig.horizontalPosition,
      verticalPosition: finalConfig.verticalPosition,
      panelClass: ['warning-snackbar'],
    });
  }

  /**
   * Show a confirmation dialog with custom actions
   */
  showConfirmation(title: string, message: string, config: ConfirmationConfig): MatSnackBarRef<AbookSnackbarComponent> {
    const finalConfig = {
      ...this.defaultConfig,
      duration: 0, // Keep open until dismissed
      actionText: 'Bekræft',
      cancelText: 'Annuller',
      ...config,
    };

    return this.snackBar.openFromComponent(AbookSnackbarComponent, {
      data: {
        title,
        message,
        actionOneText: finalConfig.actionText,
        actionOne: () => {
          finalConfig.onConfirm();
        },
        actionDismissText: finalConfig.cancelText,
      },
      duration: finalConfig.duration,
      horizontalPosition: finalConfig.horizontalPosition,
      verticalPosition: finalConfig.verticalPosition,
      panelClass: ['confirmation-snackbar'],
    });
  }

  /**
   * Show a custom snackbar with full control over data and configuration
   */
  showCustom(
    data: AbookSnackbarData,
    config?: SnackbarConfig,
  ): MatSnackBarRef<AbookSnackbarComponent> {
    const finalConfig = { ...this.defaultConfig, ...config };

    return this.snackBar.openFromComponent(AbookSnackbarComponent, {
      data,
      duration: finalConfig.duration,
      horizontalPosition: finalConfig.horizontalPosition,
      verticalPosition: finalConfig.verticalPosition,
    });
  }
}
